/**
 * Version display component for showing app version information
 */

import {
  formatVersionDisplay,
  getVersionInfo,
  getDevelopmentPhase,
  getVersionMilestone,
} from '@/config/version'

interface VersionDisplayProps {
  variant?: 'full' | 'compact' | 'badge'
  showBuildTime?: boolean
  showMilestone?: boolean
  className?: string
}

export function VersionDisplay({
  variant = 'compact',
  showBuildTime = false,
  showMilestone = false,
  className = '',
}: VersionDisplayProps) {
  const versionInfo = getVersionInfo()
  const phase = getDevelopmentPhase()
  const milestone = getVersionMilestone()
  const formattedVersion = formatVersionDisplay(versionInfo.version)

  const phaseColors = {
    alpha: 'text-red-600 bg-red-50',
    beta: 'text-yellow-600 bg-yellow-50',
    stable: 'text-green-600 bg-green-50',
  }

  if (variant === 'badge') {
    return (
      <span
        className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${phaseColors[phase]} ${className}`}
        title={milestone}
      >
        {formattedVersion} {phase !== 'stable' && phase}
      </span>
    )
  }

  if (variant === 'full') {
    return (
      <div className={`space-y-2 text-sm ${className}`}>
        <div className="flex items-center gap-2">
          <span className="font-medium">Version:</span>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${phaseColors[phase]}`}>
            {formattedVersion} {phase !== 'stable' && phase}
          </span>
        </div>

        {showMilestone && milestone && (
          <div className="text-muted-foreground">
            <span className="font-medium">Milestone:</span> {milestone}
          </div>
        )}

        {showBuildTime && (
          <div className="text-muted-foreground text-xs">
            Built: {new Date(versionInfo.buildTime).toLocaleString()}
          </div>
        )}
      </div>
    )
  }

  // Compact variant (default)
  return (
    <span className={`text-sm text-muted-foreground ${className}`} title={milestone}>
      {formattedVersion}
    </span>
  )
}
